/**
 * Country-to-language mapping for travel content translation
 * Users can translate page content to their country's native language while keeping English as primary
 */

// Map country codes to their primary native language codes (ISO 639-1 + ISO 3166-1 alpha-2)
export const COUNTRY_LANGUAGE_MAP: Record<string, { code: string; name: string; native: string }> = {
  // Europe
  NL: { code: 'nl', name: 'Dutch', native: 'Nederlands' },
  BE: { code: 'nl', name: 'Dutch', native: 'Nederlands' },
  ES: { code: 'es', name: 'Spanish', native: 'Español' },
  FR: { code: 'fr', name: 'French', native: 'Français' },
  DE: { code: 'de', name: 'German', native: 'Deutsch' },
  IT: { code: 'it', name: 'Italian', native: 'Italiano' },
  PT: { code: 'pt', name: 'Portuguese', native: 'Português' },
  GR: { code: 'el', name: 'Greek', native: 'Ελληνικά' },
  PL: { code: 'pl', name: 'Polish', native: 'Polski' },
  CZ: { code: 'cs', name: 'Czech', native: 'Čeština' },
  AT: { code: 'de', name: 'German', native: 'Deutsch' },

  // Americas
  US: { code: 'en', name: 'English', native: 'English' },
  CA: { code: 'en', name: 'English', native: 'English' },
  MX: { code: 'es', name: 'Spanish', native: 'Español' },
  BR: { code: 'pt', name: 'Portuguese', native: 'Português' },
  AR: { code: 'es', name: 'Spanish', native: 'Español' },

  // Middle East & Asia
  LB: { code: 'ar', name: 'Arabic', native: 'العربية' },
  IL: { code: 'he', name: 'Hebrew', native: 'עברית' },
  JO: { code: 'ar', name: 'Arabic', native: 'العربية' },
  IN: { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  TH: { code: 'th', name: 'Thai', native: 'ไทย' },

  // Africa
  ZA: { code: 'af', name: 'Afrikaans', native: 'Afrikaans' },
  MA: { code: 'ar', name: 'Arabic', native: 'العربية' },
};

/**
 * Get language info for a country
 */
export function getCountryLanguage(countryCode: string): { code: string; name: string; native: string } | null {
  const normalized = countryCode.toUpperCase();
  return COUNTRY_LANGUAGE_MAP[normalized] || null;
}

/**
 * Translate content to a target language using Claude API
 * Assumes env vars: VITE_CLAUDE_API_KEY (frontend) or server-side proxy
 */
export async function translateContent(
  content: string,
  targetLanguageCode: string,
  targetLanguageName: string
): Promise<string> {
  try {
    // Call backend translation endpoint instead of directly calling Claude
    const response = await fetch('/api/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content,
        target_language_code: targetLanguageCode,
        target_language_name: targetLanguageName,
      }),
    });

    if (!response.ok) {
      throw new Error(`Translation failed: ${response.status}`);
    }

    const data = await response.json();
    return data.translated_content || content;
  } catch (error) {
    console.error('Translation error:', error);
    // Return original content if translation fails
    return content;
  }
}

/**
 * Cache translations in localStorage to avoid redundant API calls
 */
const CACHE_KEY_PREFIX = 'translation_cache_';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export function getCachedTranslation(contentHash: string, languageCode: string): string | null {
  try {
    const key = `${CACHE_KEY_PREFIX}${languageCode}_${contentHash}`;
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { translation, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }

    return translation;
  } catch {
    return null;
  }
}

export function cacheTranslation(contentHash: string, languageCode: string, translation: string): void {
  try {
    const key = `${CACHE_KEY_PREFIX}${languageCode}_${contentHash}`;
    localStorage.setItem(
      key,
      JSON.stringify({
        translation,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.warn('Failed to cache translation:', error);
  }
}

/**
 * Simple hash function for content (for cache keys)
 */
export function hashContent(content: string): string {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}
